
import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import './Preserve.css';
import './Buttons.css';
import './Instructions.css';
import './CropProfile.css';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';


const PreservationTechnique = ({
    mode = 'create',
    currentUser,
    technique = null,
    selectedCrop,
    selectedPreserve,
    onClose,
    onSaved,
    onPantrySaved
}) => {
    const isViewMode = mode === 'view';

    const [isEditing, setIsEditing] = useState(false);

    const isReadOnly = isViewMode && !isEditing;

    const currentUserID = Number(
        currentUser?.UserID ??
        currentUser?.userID
    );

    const recipeOwnerID = Number(
        technique?.CreatedBy ??
        technique?.createdBy
    );
    console.log("CURRENTUSER AND RECIPEOWNER: ", currentUserID, recipeOwnerID);
    const recipeID =
        technique?.PreservationTechniqueID ??
        technique?.preservationTechniqueID;

    const isOwnRecipe =
        Number.isInteger(currentUserID) &&
        currentUserID > 0 &&
        Number.isInteger(recipeOwnerID) &&
        recipeOwnerID > 0 &&
        currentUserID === recipeOwnerID;

    const canEditRecipe =
        isViewMode &&
        Boolean(recipeID) &&
        isOwnRecipe;
    const recipeEditorRef = useRef(null);


    const editorImageInputRef = useRef(null);

    const formatRecipe = (command, value = null) => {
        if (isReadOnly || !recipeEditorRef.current) return;

        recipeEditorRef.current.focus();

        document.execCommand(command, false, value);

        setRecipe(recipeEditorRef.current.innerHTML);
    };

    const insertRecipeLink = () => {
        if (isReadOnly) return;

        const url = window.prompt('Enter the web address for your link:');

        if (!url) return;

        if (!/^https?:\/\//i.test(url)) {
            setLoadError('Please enter a link starting with https:// or http://');
            return;
        }

        formatRecipe('createLink', url);
    };

    const insertRecipeImage = (event) => {
        const file = event.target.files?.[0];

        if (!file || !file.type.startsWith('image/')) return;

        const reader = new FileReader();

        reader.onload = () => {
            formatRecipe('insertImage', reader.result);
        };

        reader.readAsDataURL(file);

        event.target.value = '';
    };


    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [recipe, setRecipe] = useState('');
    const [notes, setNotes] = useState('');

    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [userRecipe, setUserRecipe] = useState(null);
    const [rating, setRating] = useState(0);
    const [userNotes, setUserNotes] = useState('');
    const [savingUserRecipe, setSavingUserRecipe] = useState(false);

    const [amount, setAmount] = useState('');
    const [unit, setUnit] = useState('');
    const [datePreserved, setDatePreserved] = useState(
        new Date().toLocaleDateString('en-CA')
    );

    const [dateConfirmed, setDateConfirmed] = useState(false);
    const [savingPantry, setSavingPantry] = useState(false);

    const cropID =
        selectedCrop?.PreservableCropID ??
        selectedCrop?.preservableCropID;

    const preserveID =
        selectedPreserve?.PreserveID ??
        selectedPreserve?.preserveID ??
        selectedPreserve?.PreserveMethodID ??
        selectedPreserve?.preserveMethodID;

    const cropName =
        selectedCrop?.Name ??
        selectedCrop?.name ??
        'Selected crop';

    const preserveName =
        selectedPreserve?.Name ??
        selectedPreserve?.name ??
        selectedPreserve?.MethodName ??
        selectedPreserve?.methodName ??
        'Selected preservation method';

    // Load the selected record in view mode.
    // A new record always starts with blank fields.
    useEffect(() => {
        setIsEditing(false);

        if (isViewMode && technique) {
            setName(technique.Name ?? technique.name ?? '');
            setDescription(
                technique.Description ?? technique.description ?? ''
            );
            setRecipe(technique.Recipe ?? technique.recipe ?? '');
            setNotes(technique.Notes ?? technique.notes ?? '');
        } else {
            setName('');
            setDescription('');
            setRecipe('');
            setNotes('');
        }

        if (recipeEditorRef.current) {
            recipeEditorRef.current.innerHTML =
                isViewMode && technique
                    ? technique.Recipe ?? technique.recipe ?? ''
                    : '';
        }

        setLoadError('');
        setSuccessMessage('');
    }, [mode, technique]);

    useEffect(() => {
        let cancelled = false;

        const loadUserRecipe = async () => {
            setUserRecipe(null);
            setRating(0);
            setUserNotes('');

            if (!isViewMode || !recipeID || isOwnRecipe) {
                return;
            }

            try {
                const response = await api.get(
                    `/preserve/user-recipe/${recipeID}`
                );

                if (cancelled) return;

                const savedRecipe = response.data;
                setUserRecipe(savedRecipe);

                if (savedRecipe) {
                    setRating(
                        parseInt(savedRecipe.Favourite, 10) || 0
                    );

                    setUserNotes(savedRecipe.Notes ?? '');
                }
            } catch (error) {
                if (cancelled) return;

                console.error(
                    'Error loading user preservation recipe:',
                    error
                );
            }
        };

        loadUserRecipe();

        return () => {
            cancelled = true;
        };
    }, [isViewMode, recipeID, isOwnRecipe]);
    const handleCreate = async (event) => {
        event.preventDefault();

        if (isViewMode || saving) return;

        if (!cropID || !preserveID) {
            setLoadError(
                'The crop, or preservation method is missing.'
            );
            return;
        }

        if (!name.trim()) {
            setLoadError('Please enter a technique name.');
            return;
        }

        setSaving(true);
        setLoadError('');
        setSuccessMessage('');

        try {
            const response = await api.post('/preserve/techniques', {
                Name: name.trim(),
                Description: description.trim(),
                Recipe: recipe.trim(),
                Notes: notes.trim(),
                PreservableCropID: cropID,
                PreserveID: preserveID
            });

            setSuccessMessage('Preservation technique created.');

            if (onSaved) {
                onSaved(response.data);
            }
        } catch (error) {
            console.error(
                'Error creating preservation technique:',
                error
            );

            setLoadError(
                error.response?.data?.error ??
                'Unable to create the preservation technique.'
            );
        } finally {
            setSaving(false);
        }
    };



    const handleUpdateRecipe = async () => {
        if (!canEditRecipe || !isEditing || saving) {
            return;
        }

        const recipeHTML = recipeEditorRef.current?.innerHTML || '';

        const recipeText = recipeHTML
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim();

        if (!name.trim()) {
            setLoadError('Please enter a recipe name.');
            return;
        }

        if (!recipeText) {
            setLoadError('Please enter some recipe text.');
            return;
        }

        setSaving(true);
        setLoadError('');
        setSuccessMessage('');

        try {
            const response = await api.put(
                `/preserve/techniques/${technique.PreservationTechniqueID}`,
                {
                    Name: name.trim(),
                    Description: description.trim() || null,
                    Recipe: recipeHTML,
                    Notes: notes.trim() || null
                }
            );

            const updatedTechnique = response.data;

            // Keep the existing parent refresh behaviour.
            await onSaved?.(updatedTechnique);

            setIsEditing(false);
            setSuccessMessage('Recipe updated successfully.');
        } catch (error) {
            console.error('Error updating preservation recipe:', error);

            setLoadError(
                error.response?.data?.error ??
                'Unable to update the recipe.'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleSaveUserRecipe = async () => {
        if (!technique?.PreservationTechniqueID || savingUserRecipe) {
            return;
        }

        setSavingUserRecipe(true);
        setLoadError('');
        setSuccessMessage('');

        try {
            const response = await api.post('/preserve/user-recipe', {
                PreservationTechniqueID:
                    technique.PreservationTechniqueID,
                Favourite: rating > 0 ? String(rating) : null,
                Notes: userNotes.trim() || null
            });

            setUserRecipe(response.data);

            setSuccessMessage('Saved to My Recipes.');
        } catch (error) {
            console.error(
                'Error saving user preservation recipe:',
                error
            );

            setLoadError(
                error.response?.data?.error ??
                'Unable to save your recipe.'
            );
        } finally {
            setSavingUserRecipe(false);
        }
    };


    const handleSavePantry = async () => {
        if (!recipeID) {
            setLoadError('The preservation technique is missing.');
            return;
        }

        if (
            !amount.trim() ||
            !Number.isFinite(Number(amount)) ||
            Number(amount) <= 0
        ) {
            setLoadError('Please enter a valid amount produced.');
            return;
        }

        if (!unit.trim()) {
            setLoadError('Please enter a unit, such as jars or bags.');
            return;
        }

        if (!datePreserved || !dateConfirmed) {
            setLoadError(
                'Please confirm the date this food was preserved.'
            );
            return;
        }

        setSavingPantry(true);
        setLoadError('');
        setSuccessMessage('');

        try {
            await api.post('/pantryInfo', {
                PreservableCropID: cropID,
                PreserveID: preserveID,
                PreservationTechniqueID: recipeID,
                Amount: amount.trim(),
                Unit: unit.trim(),
                DatePreserved: datePreserved
            });

            // Refresh the parent sidebar immediately after saving.
            if (onPantrySaved) {
                await onPantrySaved();
            }

            setSuccessMessage('Preserved food added to your pantry.');

            setAmount('');
            setUnit('');
            setDatePreserved(new Date().toLocaleDateString('en-CA'));
            setDateConfirmed(false);
        } catch (error) {
            console.error('Error saving pantry record:', error);

            setLoadError(
                error.response?.data?.error ??
                'Unable to save the pantry record.'
            );
        } finally {
            setSavingPantry(false);
        }
    };

    const titleCase = (value) =>
        String(value || '')
            .replace(/\b\w/g, (letter) => letter.toUpperCase());

    const techniqueTitle =
        `How to ${titleCase(preserveName)} ${titleCase(cropName)}`;

    return (
        <div className="preservation-technique-page">
            <div className="preservation-technique-header">
                <h1>{techniqueTitle}</h1>
            </div>

            {loadError && (
                <div className="crop-profile-box">
                    <p role="alert">{loadError}</p>
                </div>
            )}

            {successMessage && (
                <p role="status">{successMessage}</p>
            )}


            <div className="preservation-name-field">
                <label htmlFor="preservation-recipe-name">
                    Recipe Name
                </label>

                <input
                    id="preservation-recipe-name"
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Enter a name for your recipe"
                    readOnly={isReadOnly}
                />
            </div>


            <div
                className={`recipe-toolbar ${isReadOnly ? 'disabled' : ''}`}
                role="toolbar"
                aria-label="Recipe formatting"
            >

                <select
                    aria-label="Text style"
                    value="Normal text"
                    disabled={isReadOnly}
                    onChange={(event) => {
                        const tag = event.target.value;
                        if (tag) {
                            formatRecipe('formatBlock', `<${tag}>`);
                        }
                    }}
                >
                    <option value="P">Normal text</option>
                    <option value="H2">Heading 2</option>
                    <option value="H3">Heading 3</option>
                    <option value="BLOCKQUOTE">Quote</option>
                </select>

                <button
                    type="button"
                    title="Bold"
                    aria-label="Bold"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('bold')}
                >
                    <b>B</b>
                </button>

                <button
                    type="button"
                    title="Italic"
                    aria-label="Italic"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('italic')}
                >
                    <i>I</i>
                </button>

                <button
                    type="button"
                    title="Underline"
                    aria-label="Underline"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('underline')}
                >
                    <u>U</u>
                </button>

                <span className="recipe-toolbar-divider" />

                <button
                    type="button"
                    title="Bulleted list"
                    aria-label="Bulleted list"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('insertUnorderedList')}
                >
                    Bullet List
                </button>

                <button
                    type="button"
                    title="Numbered list"
                    aria-label="Numbered list"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('insertOrderedList')}
                >
                    Number List
                </button>

                <span className="recipe-toolbar-divider" />

                <button
                    type="button"
                    title="Align left"
                    aria-label="Align left"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('justifyLeft')}
                >
                    Align left
                </button>

                <button
                    type="button"
                    title="Centre"
                    aria-label="Centre"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('justifyCenter')}
                >
                    Center
                </button>

                <button
                    type="button"
                    title="Align right"
                    aria-label="Align right"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('justifyRight')}
                >
                    Align right
                </button>

                <span className="recipe-toolbar-divider" />

                <button
                    type="button"
                    title="Insert link"
                    aria-label="Insert link"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={insertRecipeLink}
                >
                    Link
                </button>

                <button
                    type="button"
                    title="Insert image"
                    aria-label="Insert image"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => editorImageInputRef.current?.click()}
                >
                    Image
                </button>

                <input
                    ref={editorImageInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={insertRecipeImage}
                />

                <span className="recipe-toolbar-divider" />

                <button
                    type="button"
                    title="Undo"
                    aria-label="Undo"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('undo')}
                >
                    Undo
                </button>

                <button
                    type="button"
                    title="Redo"
                    aria-label="Redo"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('redo')}
                >
                    Redo
                </button>

                <button
                    type="button"
                    title="Remove formatting"
                    aria-label="Remove formatting"
                    disabled={isReadOnly}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => formatRecipe('removeFormat')}
                >
                    Clear
                </button>
            </div>


            <div
                ref={recipeEditorRef}
                className={`preservation-recipe-editor ${isReadOnly ? 'read-only' : ''
                    }`}
                contentEditable={!isReadOnly}
                suppressContentEditableWarning
                role="textbox"
                aria-label="Recipe editor"
                aria-multiline="true"
                data-placeholder="Write or paste your recipe here. Include ingredients, instructions, tips, and pictures..."
                onInput={(event) => {
                    setRecipe(event.currentTarget.innerHTML);
                }}
                onPaste={(event) => {
                    if (isReadOnly) {
                        event.preventDefault();
                        return;
                    }

                    const imageItem = Array.from(
                        event.clipboardData?.items || []
                    ).find((item) => item.type.startsWith('image/'));

                    if (!imageItem) return;

                    const file = imageItem.getAsFile();
                    if (!file) return;

                    event.preventDefault();

                    const reader = new FileReader();

                    reader.onload = () => {
                        recipeEditorRef.current?.focus();
                        document.execCommand(
                            'insertImage',
                            false,
                            reader.result
                        );
                        setRecipe(
                            recipeEditorRef.current?.innerHTML || ''
                        );
                    };

                    reader.readAsDataURL(file);
                }}
            />

            <div className="preservation-technique-actions">
                {!isViewMode && (
                    <button
                        type="button"
                        className="button"
                        disabled={saving}
                        onClick={async () => {
                            const recipeHTML =
                                recipeEditorRef.current?.innerHTML || '';

                            if (!recipeHTML.trim()) {
                                setLoadError('Please enter your recipe.');
                                return;
                            }

                            setRecipe(recipeHTML);

                            const recipeText = recipeHTML
                                .replace(/<[^>]*>/g, '')
                                .replace(/&nbsp;/g, ' ')
                                .trim();

                            if (!recipeText) {
                                setLoadError(
                                    'Please enter some recipe text as well as any pictures.'
                                );
                                return;
                            }

                            setSaving(true);
                            setLoadError('');
                            setSuccessMessage('');

                            try {
                                const response = await api.post(
                                    '/preserve/techniques',
                                    {
                                        Name: name.trim(),
                                        Description: null,
                                        Recipe: recipeHTML,
                                        Notes: null,
                                        PreservableCropID: cropID,
                                        PreserveID: preserveID
                                    }
                                );

                                setSuccessMessage(
                                    'Recipe created successfully.'
                                );

                                await onSaved?.(response.data);
                            } catch (error) {
                                console.error(
                                    'Error creating recipe:',
                                    error
                                );

                                setLoadError(
                                    error.response?.data?.error ||
                                    'Unable to save your recipe.'
                                );
                            } finally {
                                setSaving(false);
                            }
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Recipe'}
                    </button>
                )}

                {isViewMode && canEditRecipe && !isEditing && (
                    <button
                        type="button"
                        className="button"
                        onClick={() => {
                            setIsEditing(true);
                            setLoadError('');
                            setSuccessMessage('');
                        }}
                    >
                        Edit Recipe
                    </button>
                )}

                {isViewMode && canEditRecipe && isEditing && (
                    <>
                        <button
                            type="button"
                            className="button"
                            onClick={handleUpdateRecipe}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>

                        <button
                            type="button"
                            className="button"
                            disabled={saving}
                            onClick={() => {
                                setName(technique.Name ?? technique.name ?? '');
                                setDescription(
                                    technique.Description ??
                                    technique.description ??
                                    ''
                                );
                                setRecipe(technique.Recipe ?? technique.recipe ?? '');
                                setNotes(technique.Notes ?? technique.notes ?? '');

                                if (recipeEditorRef.current) {
                                    recipeEditorRef.current.innerHTML =
                                        technique.Recipe ?? technique.recipe ?? '';
                                }

                                setIsEditing(false);
                                setLoadError('');
                                setSuccessMessage('');
                            }}
                        >
                            Cancel Edit
                        </button>
                    </>
                )}

                <button
                    type="button"
                    className="button"
                    onClick={onClose}
                    disabled={saving}
                >
                    {isViewMode ? 'Back' : 'Cancel'}
                </button>
            </div>


            {isViewMode && recipeID && (
                <div className="preservation-personal-section">

                    {!isOwnRecipe && (
                        <div className="preservation-rating">
                            <h2>Your Rating</h2>

                            <div
                                className="preservation-stars"
                                role="radiogroup"
                                aria-label="Rate this recipe from 1 to 5 stars"
                            >
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        className={`preservation-star ${star <= rating ? 'selected' : ''
                                            }`}
                                        onClick={() => setRating(star)}
                                        aria-label={`${star} star${star > 1 ? 's' : ''
                                            }`}
                                        aria-pressed={rating === star}
                                    >
                                        <FontAwesomeIcon icon={faStar} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="preservation-notes">
                        <h2>My Notes</h2>

                        <textarea
                            value={userNotes}
                            onChange={(event) =>
                                setUserNotes(event.target.value)
                            }
                            placeholder="Add your personal notes about this recipe..."
                            rows={5}
                        />
                    </div>

                    <div className="preservation-personal-actions">
                        <button
                            type="button"
                            className="button"
                            onClick={handleSaveUserRecipe}
                            disabled={savingUserRecipe}
                        >
                            {savingUserRecipe
                                ? 'Saving...'
                                : userRecipe
                                    ? 'Update My Recipe'
                                    : 'Save to My Recipes'}
                        </button>
                    </div>

                </div>
            )}

            {isViewMode && recipeID && (
                <div className="preservation-personal-section">
                    <h2>Add to My Preserved Pantry Items</h2>

                    <p>
                        Record how much {cropName} you preserved using
                        this technique.
                    </p>

                    <div className="preservation-name-field">
                        <label htmlFor="pantry-amount">
                            Amount Produced
                        </label>

                        <input
                            id="pantry-amount"
                            type="number"
                            min="0.01"
                            step="any"
                            value={amount}
                            onChange={(event) => setAmount(event.target.value)}
                            placeholder="e.g. 12"
                        />
                    </div>

                    <div className="preservation-name-field">
                        <label htmlFor="pantry-unit">
                            Unit
                        </label>

                        <input
                            id="pantry-unit"
                            type="text"
                            value={unit}
                            onChange={(event) => setUnit(event.target.value)}
                            placeholder="e.g. jars, bags, litres"
                        />
                    </div>

                    <div className="preservation-name-field">
                        <label htmlFor="date-preserved">
                            Date Preserved
                        </label>

                        <input
                            id="date-preserved"
                            type="date"
                            value={datePreserved}
                            onChange={(event) => {
                                setDatePreserved(event.target.value);
                                setDateConfirmed(false);
                            }}
                            required
                        />
                    </div>

                    <label>
                        <input
                            type="checkbox"
                            checked={dateConfirmed}
                            onChange={(event) =>
                                setDateConfirmed(event.target.checked)
                            }
                        />
                        {' '}I confirm this is the date the food was preserved.
                    </label>

                    <div className="preservation-personal-actions">
                        <button
                            type="button"
                            className="button"
                            onClick={handleSavePantry}
                            disabled={savingPantry}
                        >
                            {savingPantry
                                ? 'Saving...'
                                : 'Save to My Pantry'}
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default PreservationTechnique;
